import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Edit, Trash2, AlertCircle, CheckCircle, ExternalLink, 
  User, FileText, UserPlus, MessageCircle, Bell, Upload, X, CreditCard,
  Home, Calendar, Award, Settings, ChevronRight, Ticket, UtensilsCrossed,
  Bed, Clock, MapPin, Mail, Phone, GraduationCap, Building, Gift, Rocket, History,
  Brain, Layers, ArrowRight, Trophy, Zap
} from 'lucide-react';
import axios from 'axios';
import TeamIntentForm from '../components/TeamIntentForm';
import AccommodationBooking from '../components/AccommodationBooking';
import ConfirmationLetter from '../components/ConfirmationLetter';
import CashfreeCheckout from '../components/CashfreeCheckout';
import TeamSubmissionSection from '../components/TeamSubmissionSection';
import TeamManagement from '../components/TeamManagement';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to safely extract error message from API responses
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors: [{loc, msg, type}, ...]
    // Group by field and deduplicate messages
    const errorsByField = {};
    detail.forEach(e => {
      const loc = e.loc || [];
      // Get the field name (last part of loc, excluding 'body' and indices)
      const fieldParts = loc.filter(p => typeof p === 'string' && p !== 'body');
      const fieldName = fieldParts.length > 0 ? fieldParts[fieldParts.length - 1] : 'field';
      const msg = e.msg || e.message || 'Invalid value';
      
      if (!errorsByField[fieldName]) {
        errorsByField[fieldName] = new Set();
      }
      errorsByField[fieldName].add(msg);
    });
    
    // Format the errors nicely
    const errorMessages = Object.entries(errorsByField).map(([field, msgs]) => {
      const msgArray = Array.from(msgs);
      const formattedField = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
      if (msgArray.length === 1 && msgArray[0].toLowerCase().includes('required')) {
        return `${formattedField} is required`;
      }
      return `${formattedField}: ${msgArray.join(', ')}`;
    });
    
    // Deduplicate final messages
    const uniqueMessages = [...new Set(errorMessages)];
    return uniqueMessages.length > 3 
      ? `${uniqueMessages.slice(0, 3).join('. ')}. And ${uniqueMessages.length - 3} more errors.`
      : uniqueMessages.join('. ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

// Tab Navigation Component
const TabNavigation = ({ activeTab, setActiveTab, team }) => {
  const tabs = [
    { id: 'programs', label: 'My Programs', icon: Layers },
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'team', label: 'Team Details', icon: Users },
    { id: 'submissions', label: 'Submissions', icon: Rocket, requiresPaid: true },
    { id: 'bookings', label: 'Bookings', icon: Bed, requiresPaid: true },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1.5 mb-6">
      <div className="flex flex-wrap gap-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isDisabled = tab.requiresPaid && (!team || team.payment_status !== 'completed');
          
          return (
            <button
              key={tab.id}
              onClick={() => !isDisabled && setActiveTab(tab.id)}
              disabled={isDisabled}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all text-sm
                ${activeTab === tab.id 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : isDisabled 
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={18} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Quick Stats Card Component
const StatCard = ({ icon: Icon, label, value, color = 'blue', subtext }) => {
  const colorClasses = {
    blue: { text: 'text-blue-600', bg: 'bg-blue-50', icon: 'text-blue-500' },
    green: { text: 'text-green-600', bg: 'bg-green-50', icon: 'text-green-500' },
    purple: { text: 'text-purple-600', bg: 'bg-purple-50', icon: 'text-purple-500' },
    orange: { text: 'text-orange-600', bg: 'bg-orange-50', icon: 'text-orange-500' },
    yellow: { text: 'text-yellow-600', bg: 'bg-yellow-50', icon: 'text-yellow-500' },
    red: { text: 'text-red-600', bg: 'bg-red-50', icon: 'text-red-500' },
  };
  const colors = colorClasses[color] || colorClasses.blue;
  
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold ${colors.text} mt-1`}>{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-2.5 ${colors.bg} rounded-lg`}>
          <Icon className={colors.icon} size={20} />
        </div>
      </div>
    </div>
  );
};

// Member Card Component
const MemberCard = ({ member, index, certificates, backendUrl }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 hover:border-blue-200 hover:shadow-md transition-all">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold text-gray-900">{member.name}</h4>
          {member.participant_id && (
            <span className="text-xs font-mono bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
              {member.participant_id}
            </span>
          )}
        </div>
        <div className="mt-2 space-y-1">
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Mail size={14} /> {member.email}
          </p>
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Phone size={14} /> {member.phone}
          </p>
          {member.college && (
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <GraduationCap size={14} /> {member.college}
            </p>
          )}
        </div>
        {member.workshop_choice && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-xs bg-purple-50 text-purple-600 px-2.5 py-1 rounded-full font-medium">
              📚 {member.workshop_choice}
            </span>
            {member.mode && (
              <span className="text-xs bg-green-50 text-green-600 px-2.5 py-1 rounded-full font-medium">
                {member.mode === 'Offline' ? '🏛️' : '💻'} {member.mode}
              </span>
            )}
          </div>
        )}
        {/* Certificate Downloads */}
        {certificates[member.email] && (
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`${backendUrl}/api/certificates/download/${certificates[member.email].certificate_number}`}
              download
              className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-1.5"
            >
              <FileText size={12} /> PDF Certificate
            </a>
            <a
              href={`${backendUrl}/api/certificates/download-png/${certificates[member.email].certificate_number}`}
              download
              className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-1.5"
            >
              <FileText size={12} /> PNG (LinkedIn)
            </a>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Payment Status Badge Component
const PaymentStatusBadge = ({ status }) => {
  const statusConfig = {
    completed: { 
      bgColor: 'bg-green-100', 
      textColor: 'text-green-700', 
      label: 'Paid', 
      icon: CheckCircle 
    },
    pending: { 
      bgColor: 'bg-yellow-100', 
      textColor: 'text-yellow-700', 
      label: 'Payment Pending', 
      icon: Clock 
    },
    partial: { 
      bgColor: 'bg-orange-100', 
      textColor: 'text-orange-700', 
      label: 'Partial Payment', 
      icon: AlertCircle 
    },
  };
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
      <Icon size={14} />
      {config.label}
    </span>
  );
};

const DashboardPage = () => {
  const { user, loading: authLoading, initialized, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLetter, setShowLetter] = useState(false);
  const [letterData, setLetterData] = useState(null);
  const [loadingLetter, setLoadingLetter] = useState(false);
  const [memberCertificates, setMemberCertificates] = useState({});
  const [submissionEnabled, setSubmissionEnabled] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState('');
  const [activeTab, setActiveTab] = useState('programs');
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);
  
  // Theme Change Modal State
  const [showThemeChangeModal, setShowThemeChangeModal] = useState(false);
  const [newTheme, setNewTheme] = useState('');
  const [newIdeaDescription, setNewIdeaDescription] = useState('');
  const [changingTheme, setChangingTheme] = useState(false);
  
  // Participation History State
  const [participationHistory, setParticipationHistory] = useState(null);
  
  // IAPL (Cross-Program) State
  const [iaplTeam, setIaplTeam] = useState(null);
  const [iaplConfig, setIaplConfig] = useState(null);
  
  // Team leader check
  const isTeamLeader = team?.leader_id === user?.id;

  const [formData, setFormData] = useState({
    team_name: '',
    hackathon_theme: '',
    idea_description: '',
    referral_code: '',
    members: [{ name: '', email: '', phone: '', college: '', workshop_choice: '', mode: '', year: '', gender: '' }]
  });

  // Handle authentication redirect
  useEffect(() => {
    // Wait for auth to be initialized before redirecting
    if (!initialized) return;
    
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [initialized, authLoading, isAuthenticated, navigate]);

  // Fetch data when user is authenticated
  useEffect(() => {
    if (initialized && isAuthenticated && user) {
      fetchTeamAndEventInfo();
    }
  }, [initialized, isAuthenticated, user]);

  const fetchTeamAndEventInfo = async () => {
    setLoading(true);
    try {
      const [teamResponse, eventResponse, submissionResponse, historyResponse, iaplTeamRes, iaplConfigRes] = await Promise.all([
        axios.get(`${API}/team/my-team`),
        axios.get(`${API}/event-info`),
        axios.get(`${API}/settings/submission-enabled`),
        axios.get(`${API}/team/participation-history`),
        axios.get(`${API}/iapl/team/my-team`).catch(() => ({ data: { team: null } })),
        axios.get(`${API}/iapl/config`).catch(() => ({ data: { enabled: false } }))
      ]);
      
      if (teamResponse.data.team) {
        setTeam(teamResponse.data.team);
        fetchMemberCertificates(teamResponse.data.team.members);
      } else {
        setFormData(prev => ({
          ...prev,
          members: [{
            name: user?.name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            college: '',
            workshop_choice: '',
            mode: '',
            year: '',
            gender: ''
          }]
        }));
      }
      setEventInfo(eventResponse.data);
      setSubmissionEnabled(submissionResponse.data.submission_enabled || false);
      setParticipationHistory(historyResponse.data);
      setIaplTeam(iaplTeamRes.data.team);
      setIaplConfig(iaplConfigRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      if (user) {
        setFormData(prev => ({
          ...prev,
          members: [{
            name: user?.name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            college: '',
            workshop_choice: '',
            mode: '',
            year: '',
            gender: ''
          }]
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberCertificates = async (members) => {
    const certificates = {};
    for (const member of members) {
      try {
        const response = await axios.get(`${API}/certificates/check/${encodeURIComponent(member.email)}`);
        if (response.data.certificate) {
          certificates[member.email] = response.data.certificate;
        }
      } catch (err) {
        // Certificate not found
      }
    }
    setMemberCertificates(certificates);
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate team name
    if (!formData.team_name.trim()) {
      setError('Please enter a team name');
      toast.error('Please enter a team name');
      return;
    }

    // Validate hackathon theme
    if (!formData.hackathon_theme) {
      setError('Please select a hackathon theme');
      toast.error('Please select a hackathon theme');
      return;
    }

    // Validate member count
    if (formData.members.length < 2) {
      setError('Team must have at least 2 members');
      toast.error('Team must have at least 2 members');
      return;
    }

    // Validate each member's required fields
    const memberErrors = [];
    formData.members.forEach((member, index) => {
      if (!member.name.trim()) {
        memberErrors.push(`Member ${index + 1}: Name is required`);
      }
      if (!member.email.trim()) {
        memberErrors.push(`Member ${index + 1}: Email is required`);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
        memberErrors.push(`Member ${index + 1}: Please enter a valid email`);
      }
      if (!member.phone.trim()) {
        memberErrors.push(`Member ${index + 1}: Phone is required`);
      }
    });

    if (memberErrors.length > 0) {
      const errorMessage = memberErrors.length > 3 
        ? `${memberErrors.slice(0, 3).join('. ')}. And ${memberErrors.length - 3} more errors.`
        : memberErrors.join('. ');
      setError(errorMessage);
      toast.error('Please fill in all required member details');
      return;
    }

    // Check for duplicate emails
    const emails = formData.members.map(m => m.email.toLowerCase().trim());
    const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicateEmails.length > 0) {
      setError('Each team member must have a unique email address');
      toast.error('Duplicate email addresses found');
      return;
    }

    if (formData.hackathon_theme === 'Open Innovation') {
      const wordCount = formData.idea_description.split(/\s+/).filter(Boolean).length;
      if (wordCount < 50) {
        setError('Please provide a detailed description of your idea (minimum 50 words)');
        return;
      }
      if (wordCount > 1000) {
        setError('Your idea description exceeds 1000 words. Please keep it concise.');
        return;
      }
    }

    try {
      const response = await axios.post(`${API}/team/create`, {
        team_name: formData.team_name,
        hackathon_theme: formData.hackathon_theme,
        idea_description: formData.hackathon_theme === 'Open Innovation' ? formData.idea_description : '',
        members: formData.members,
        referral_code: formData.referral_code || null
      });
      
      setTeam(response.data.team);
      setShowCreateForm(false);
      
      // Show appropriate success message
      if (response.data.referral_applied) {
        setSuccess(`Team created! ₹${response.data.referral_discount} referral discount applied. Complete payment to confirm.`);
        toast.success(`₹${response.data.referral_discount} referral discount applied!`);
      } else {
        setSuccess('Team created successfully! Complete payment to confirm your registration.');
        toast.success('Team created successfully!');
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to create team');
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate each member's required fields
    const memberErrors = [];
    formData.members.forEach((member, index) => {
      if (!member.name.trim()) {
        memberErrors.push(`Member ${index + 1}: Name is required`);
      }
      if (!member.email.trim()) {
        memberErrors.push(`Member ${index + 1}: Email is required`);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
        memberErrors.push(`Member ${index + 1}: Please enter a valid email`);
      }
      if (!member.phone.trim()) {
        memberErrors.push(`Member ${index + 1}: Phone is required`);
      }
    });

    if (memberErrors.length > 0) {
      const errorMessage = memberErrors.length > 3 
        ? `${memberErrors.slice(0, 3).join('. ')}. And ${memberErrors.length - 3} more errors.`
        : memberErrors.join('. ');
      setError(errorMessage);
      toast.error('Please fill in all required member details');
      return;
    }

    // Check for duplicate emails
    const emails = formData.members.map(m => m.email.toLowerCase().trim());
    const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicateEmails.length > 0) {
      setError('Each team member must have a unique email address');
      toast.error('Duplicate email addresses found');
      return;
    }

    // Only validate Open Innovation if team is not yet paid (can still edit theme)
    if (team?.payment_status !== 'completed' && team?.payment_status !== 'partial') {
      if (formData.hackathon_theme === 'Open Innovation') {
        const wordCount = formData.idea_description.split(/\s+/).filter(Boolean).length;
        if (wordCount < 50) {
          setError('Please provide a detailed description of your idea (minimum 50 words)');
          return;
        }
        if (wordCount > 1000) {
          setError('Your idea description exceeds 1000 words. Please keep it concise.');
          return;
        }
      }
    }

    try {
      let updateData;
      
      // If payment is completed or partial, only send members (can't change name/theme)
      if (team?.payment_status === 'completed' || team?.payment_status === 'partial') {
        updateData = {
          members: formData.members
        };
      } else {
        // Payment pending - can edit everything
        updateData = {
          team_name: formData.team_name,
          hackathon_theme: formData.hackathon_theme,
          idea_description: formData.hackathon_theme === 'Open Innovation' ? formData.idea_description : '',
          members: formData.members
        };
      }

      const response = await axios.put(`${API}/team/update`, updateData);
      setTeam(response.data.team);
      setShowEditForm(false);
      setSuccess('Team updated successfully!');
      toast.success('Team updated successfully!');
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to update team');
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleApplyReferralCode = async () => {
    if (!referralCodeInput.trim()) {
      toast.error('Please enter a referral code');
      return;
    }
    
    setApplyingReferral(true);
    try {
      const response = await axios.post(`${API}/team/apply-referral`, {
        referral_code: referralCodeInput.trim()
      });
      
      setTeam(response.data.team);
      setShowReferralModal(false);
      setReferralCodeInput('');
      toast.success(response.data.message);
      setSuccess(`Referral code applied! You saved ₹${response.data.referral_discount}`);
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to apply referral code');
      toast.error(errorMsg);
    } finally {
      setApplyingReferral(false);
    }
  };

  // Handle Theme Change
  const handleThemeChange = async () => {
    if (!newTheme) {
      toast.error('Please select a theme');
      return;
    }
    
    if (newTheme === 'Open Innovation' && !newIdeaDescription.trim()) {
      toast.error('Please provide an idea description for Open Innovation theme');
      return;
    }
    
    if (newTheme === 'Open Innovation') {
      const wordCount = newIdeaDescription.trim().split(/\s+/).length;
      if (wordCount < 50) {
        toast.error(`Idea description must be at least 50 words (currently ${wordCount} words)`);
        return;
      }
      if (wordCount > 1000) {
        toast.error(`Idea description must not exceed 1000 words (currently ${wordCount} words)`);
        return;
      }
    }
    
    setChangingTheme(true);
    try {
      const response = await axios.post(`${API}/team/change-theme`, {
        new_theme: newTheme,
        idea_description: newTheme === 'Open Innovation' ? newIdeaDescription.trim() : null
      });
      
      // Refresh team data
      const teamResponse = await axios.get(`${API}/team/my-team`);
      setTeam(teamResponse.data.team);
      
      setShowThemeChangeModal(false);
      setNewTheme('');
      setNewIdeaDescription('');
      toast.success(response.data.message);
      
      if (response.data.team.problem_statement_cleared) {
        toast.info('Your problem statement selection has been cleared. Please select a new one from the Submissions tab.');
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to change theme');
      toast.error(errorMsg);
    } finally {
      setChangingTheme(false);
    }
  };

  const openThemeChangeModal = () => {
    setNewTheme(team?.hackathon_theme || '');
    setNewIdeaDescription(team?.idea_description || '');
    setShowThemeChangeModal(true);
  };

  const handleDeleteTeam = async () => {
    if (!window.confirm('Are you sure you want to delete your team? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/team/delete`);
      setTeam(null);
      setSuccess('Team deleted successfully');
      toast.success('Team deleted successfully');
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to delete team');
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const addMember = () => {
    if (formData.members.length < 10) {
      setFormData({
        ...formData,
        members: [...formData.members, { name: '', email: '', phone: '', college: '', workshop_choice: '', mode: '', year: '', gender: '' }]
      });
    }
  };

  const removeMember = (index) => {
    if (formData.members.length > 1) {
      const newMembers = formData.members.filter((_, i) => i !== index);
      setFormData({ ...formData, members: newMembers });
    }
  };

  const updateMember = (index, field, value) => {
    const newMembers = [...formData.members];
    newMembers[index] = { ...newMembers[index], [field]: value };
    setFormData({ ...formData, members: newMembers });
  };

  const startEdit = () => {
    setFormData({
      team_name: team.team_name,
      hackathon_theme: team.hackathon_theme || '',
      idea_description: team.idea_description || '',
      members: team.members.map(m => ({
        name: m.name || '',
        email: m.email || '',
        phone: m.phone || '',
        college: m.college || '',
        workshop_choice: m.workshop_choice || '',
        mode: m.mode || '',
        year: m.year || '',
        gender: m.gender || ''
      }))
    });
    setShowEditForm(true);
    setError('');
    setSuccess('');
  };

  const fetchConfirmationLetter = async () => {
    setLoadingLetter(true);
    try {
      const response = await axios.get(`${API}/team/confirmation-letter`);
      setLetterData(response.data);
      setShowLetter(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to generate confirmation letter'));
    } finally {
      setLoadingLetter(false);
    }
  };

  const handlePaymentSuccess = (data) => {
    setShowPaymentModal(false);
    setSuccess('Payment successful! Your registration is confirmed.');
    toast.success('Payment successful!');
    fetchTeamAndEventInfo();
  };

  const handlePaymentFailure = (error) => {
    setShowPaymentModal(false);
    setError(error?.error || error || 'Payment failed. Please try again.');
    toast.error('Payment failed. Please try again.');
  };

  // Show loading while auth is initializing or data is being fetched
  if (!initialized || authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // If not authenticated after loading, don't render (will be redirected)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Student Dashboard</h1>
              <p className="text-sm text-gray-500 mt-1">Welcome back, {user?.name || 'Participant'}</p>
            </div>
            {team && (
              <div className="flex items-center gap-3">
                <PaymentStatusBadge status={team.payment_status} />
                {team.payment_status === 'completed' && submissionEnabled && (
                  <button
                    onClick={() => navigate('/submission')}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <Upload size={16} />
                    Submit Project
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="error-alert">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
              <X size={16} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3" data-testid="success-alert">
            <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-green-700 text-sm">{success}</p>
            <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-600">
              <X size={16} />
            </button>
          </div>
        )}

        {/* WhatsApp Channel Banner - Compact */}
        <div className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 shadow-md">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <MessageCircle className="text-white" size={24} />
              </div>
              <div className="text-white">
                <p className="font-semibold">Join Our WhatsApp Channel</p>
                <p className="text-green-100 text-sm">Get instant updates & announcements</p>
              </div>
            </div>
            <a
              href="https://www.whatsapp.com/channel/0029VbBw8eF3WHTOExMHU708"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-green-600 px-4 py-2 rounded-lg font-medium text-sm hover:bg-green-50 transition-colors flex items-center gap-2"
            >
              Join Now <ExternalLink size={14} />
            </a>
          </div>
        </div>

        {/* Tab Navigation - Always show for logged in users */}
        {!showCreateForm && !showEditForm && (
          <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} team={team} />
        )}

        {/* My Programs Tab - Unified Cross-Program Dashboard */}
        {activeTab === 'programs' && !showCreateForm && !showEditForm && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <Layers size={28} />
                <h2 className="text-2xl font-bold">My Programs</h2>
              </div>
              <p className="text-blue-100">Track your participation across all EvalentX programs</p>
            </div>

            {/* Program Cards Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* EvalentX Hackathon Card */}
              <div className={`bg-white rounded-xl shadow-sm border-2 transition-all hover:shadow-lg ${team ? 'border-green-200' : 'border-gray-200 hover:border-blue-300'}`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                        <Rocket className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">EvalentX – 2nd Edition</h3>
                        <p className="text-sm text-gray-500">IIT Gandhinagar Hackathon</p>
                      </div>
                    </div>
                    {team && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        team.payment_status === 'completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {team.payment_status === 'completed' ? 'Registered' : 'Pending Payment'}
                      </span>
                    )}
                  </div>

                  {team ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users size={16} />
                        <span>Team: <strong className="text-gray-900">{team.team_name}</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Award size={16} />
                        <span>Theme: <strong className="text-gray-900">{team.hackathon_theme}</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={16} />
                        <span>11–12 April 2026</span>
                      </div>
                      <button
                        onClick={() => setActiveTab('overview')}
                        className="w-full mt-4 bg-blue-50 text-blue-600 px-4 py-2.5 rounded-lg font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                      >
                        View Dashboard
                        <ArrowRight size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        Join India's premier hackathon with ₹4,50,000 prize pool across AI, Blockchain, and Open Innovation tracks.
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-gray-500">
                          <Trophy size={14} className="text-yellow-500" />
                          <span>₹4.5L Prizes</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Calendar size={14} />
                          <span>11-12 Apr 2026</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowCreateForm(true)}
                        className="w-full mt-4 bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2.5 rounded-lg font-medium hover:from-red-600 hover:to-orange-600 transition-all flex items-center justify-center gap-2"
                        data-testid="join-hackathon-btn"
                      >
                        <Plus size={18} />
                        Register Now
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* IAPL Card */}
              <div className={`bg-white rounded-xl shadow-sm border-2 transition-all hover:shadow-lg ${iaplTeam ? 'border-purple-200' : 'border-gray-200 hover:border-purple-300'}`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                        <Brain className="text-white" size={24} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900">AI League (IAPL)</h3>
                          <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">LIVE</span>
                        </div>
                        <p className="text-sm text-gray-500">India's AI Premium League</p>
                      </div>
                    </div>
                    {iaplTeam && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        iaplTeam.payment_status === 'completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {iaplTeam.payment_status === 'completed' ? 'Registered' : 'Pending Payment'}
                      </span>
                    )}
                  </div>

                  {!iaplConfig?.enabled ? (
                    <div className="space-y-3">
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <Clock size={24} className="text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 font-medium">Registration Closed</p>
                        <p className="text-xs text-gray-500">Check back later for updates</p>
                      </div>
                    </div>
                  ) : iaplTeam ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users size={16} />
                        <span>Team: <strong className="text-gray-900">{iaplTeam.team_name}</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Zap size={16} />
                        <span>Focus: <strong className="text-gray-900">{iaplTeam.ai_focus_area}</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={16} />
                        <span>Finals: 12 April 2026</span>
                      </div>
                      <button
                        onClick={() => navigate('/iapl-dashboard')}
                        className="w-full mt-4 bg-purple-50 text-purple-600 px-4 py-2.5 rounded-lg font-medium hover:bg-purple-100 transition-colors flex items-center justify-center gap-2"
                      >
                        View IAPL Dashboard
                        <ArrowRight size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        Compete in India's most prestigious AI competition with ₹5,00,000 prize pool.
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-gray-500">
                          <Trophy size={14} className="text-purple-500" />
                          <span>₹5L Prizes</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Calendar size={14} />
                          <span>Mar 2026</span>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/iapl-dashboard')}
                        className="w-full mt-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-2.5 rounded-lg font-medium hover:from-purple-600 hover:to-blue-600 transition-all flex items-center justify-center gap-2"
                        data-testid="join-iapl-btn"
                      >
                        <Plus size={18} />
                        Join AI League
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Unified Timeline */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-blue-600" />
                Upcoming Events Timeline
              </h3>
              <div className="space-y-4">
                {iaplConfig?.enabled && (
                  <>
                    <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="w-16 text-center">
                        <p className="text-xs text-purple-600 font-medium">MAR</p>
                        <p className="text-xl font-bold text-purple-700">15-20</p>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">IAPL Online Training</p>
                        <p className="text-sm text-gray-500">AI League preparation phase</p>
                      </div>
                      <Brain size={20} className="text-purple-500" />
                    </div>
                    <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="w-16 text-center">
                        <p className="text-xs text-purple-600 font-medium">MAR</p>
                        <p className="text-xl font-bold text-purple-700">20-23</p>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">IAPL Product Development</p>
                        <p className="text-sm text-gray-500">Build your AI solution</p>
                      </div>
                      <Zap size={20} className="text-purple-500" />
                    </div>
                  </>
                )}
                <div className="flex items-center gap-4 p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <div className="w-16 text-center">
                    <p className="text-xs text-orange-600 font-medium">MAR</p>
                    <p className="text-xl font-bold text-orange-700">28</p>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">EvalentX Workshops</p>
                    <p className="text-sm text-gray-500">AI & Blockchain expert sessions</p>
                  </div>
                  <Award size={20} className="text-orange-500" />
                </div>
                <div className="flex items-center gap-4 p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="w-16 text-center">
                    <p className="text-xs text-red-600 font-medium">MAR</p>
                    <p className="text-xl font-bold text-red-700">28-29</p>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">EvalentX Hackathon</p>
                    <p className="text-sm text-gray-500">24-hour coding marathon @ IIT Gandhinagar</p>
                  </div>
                  <Rocket size={20} className="text-red-500" />
                </div>
                {iaplConfig?.enabled && (
                  <div className="flex items-center gap-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                    <div className="w-16 text-center">
                      <p className="text-xs text-purple-600 font-medium">MAR</p>
                      <p className="text-xl font-bold text-purple-700">29</p>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">IAPL Grand Finals</p>
                      <p className="text-sm text-gray-500">Final presentations @ IIT Gandhinagar</p>
                    </div>
                    <Trophy size={20} className="text-yellow-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{(team ? 1 : 0) + (iaplTeam ? 1 : 0)}</p>
                <p className="text-sm text-gray-500">Programs Joined</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
                <p className="text-3xl font-bold text-green-600">
                  {(team?.payment_status === 'completed' ? 1 : 0) + (iaplTeam?.payment_status === 'completed' ? 1 : 0)}
                </p>
                <p className="text-sm text-gray-500">Confirmed</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{participationHistory?.total_participations || 0}</p>
                <p className="text-sm text-gray-500">Total Participations</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
                <p className="text-3xl font-bold text-orange-600">2</p>
                <p className="text-sm text-gray-500">Available Programs</p>
              </div>
            </div>
          </div>
        )}

        {/* No Team - Create Team CTA (only on overview tab) */}
        {!team && !showCreateForm && activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-blue-600" size={32} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Create Your Team</h2>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Register your team to participate in EvalentX – 2nd Edition at IIT Gandhinagar
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                data-testid="create-team-btn"
              >
                <Plus size={20} />
                Create Team
              </button>
            </div>
            
            {/* Join Team Section for users without a team */}
            <TeamManagement 
              team={null} 
              onUpdate={fetchTeamAndEventInfo} 
              isLeader={false} 
            />
            
            {/* Past Participation History for users without current team */}
            {participationHistory && participationHistory.past_participations?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <History size={20} className="text-purple-600" />
                  Your Past Participations
                </h3>
                <p className="text-sm text-gray-500 mb-4">You participated in previous EvalentX editions. Your login credentials remain the same!</p>
                <div className="space-y-3">
                  {participationHistory.past_participations.map((participation, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                          {participation.edition}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{participation.team_name}</p>
                          <p className="text-sm text-gray-500">Edition {participation.edition} • {participation.hackathon_theme}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        participation.payment_status === 'completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {participation.payment_status === 'completed' ? 'Confirmed' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Team Form */}
        {showCreateForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Create Your Team</h2>
              <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateTeam} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Name *</label>
                  <input
                    type="text"
                    value={formData.team_name}
                    onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter team name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hackathon Theme *</label>
                  <select
                    value={formData.hackathon_theme}
                    onChange={(e) => setFormData({ ...formData, hackathon_theme: e.target.value, idea_description: e.target.value !== 'Open Innovation' ? '' : formData.idea_description })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Theme</option>
                    <option value="Artificial Intelligence">Artificial Intelligence</option>
                    <option value="Blockchain">Blockchain</option>
                    <option value="Open Innovation">Open Innovation</option>
                  </select>
                </div>
              </div>

              {formData.hackathon_theme === 'Open Innovation' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Describe Your Idea (50-1000 words) *</label>
                  <textarea
                    value={formData.idea_description}
                    onChange={(e) => setFormData({ ...formData, idea_description: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[150px]"
                    placeholder="Describe your innovative idea..."
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Word count: {formData.idea_description.split(/\s+/).filter(Boolean).length} / 1000
                  </p>
                </div>
              )}

              {/* Referral Code */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-emerald-700 mb-2">
                  Have a Referral Code? (Optional)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={formData.referral_code}
                    onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                    className="flex-1 px-4 py-2.5 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 uppercase"
                    placeholder="Enter referral code"
                    maxLength={12}
                    data-testid="referral-code-input"
                  />
                </div>
                <p className="text-xs text-emerald-600 mt-2">
                  Get ₹100 discount per member when you use a valid referral code!
                </p>
              </div>

              {/* Team Members */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Team Members ({formData.members.length}/10)</h3>
                  {formData.members.length < 10 && (
                    <button type="button" onClick={addMember} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                      <Plus size={16} /> Add Member
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  {formData.members.map((member, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Member {index + 1}</span>
                        {formData.members.length > 1 && (
                          <button type="button" onClick={() => removeMember(index)} className="text-red-500 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <input
                          type="text"
                          placeholder="Full Name *"
                          value={member.name}
                          onChange={(e) => updateMember(index, 'name', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="email"
                          placeholder="Email *"
                          value={member.email}
                          onChange={(e) => updateMember(index, 'email', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="tel"
                          placeholder="Phone *"
                          value={member.phone}
                          onChange={(e) => updateMember(index, 'phone', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="text"
                          placeholder="College/University"
                          value={member.college}
                          onChange={(e) => updateMember(index, 'college', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                        <select
                          value={member.year || ''}
                          onChange={(e) => updateMember(index, 'year', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Year of Study</option>
                          <option value="1st Year">1st Year</option>
                          <option value="2nd Year">2nd Year</option>
                          <option value="3rd Year">3rd Year</option>
                          <option value="4th Year">4th Year</option>
                          <option value="PG">PG</option>
                          <option value="PhD">PhD</option>
                          <option value="Working Professional">Working Professional</option>
                        </select>
                        <select
                          value={member.gender || ''}
                          onChange={(e) => updateMember(index, 'gender', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                        <select
                          value={member.workshop_choice}
                          onChange={(e) => updateMember(index, 'workshop_choice', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Workshop (Optional)</option>
                          <option value="Artificial Intelligence">Artificial Intelligence</option>
                          <option value="Blockchain">Blockchain</option>
                        </select>
                        <select
                          value={member.mode}
                          onChange={(e) => updateMember(index, 'mode', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Mode (Optional)</option>
                          <option value="Offline">Offline</option>
                          <option value="Online">Online</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  data-testid="submit-create-team"
                >
                  Create Team
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Overview Tab */}
        {team && !showCreateForm && !showEditForm && activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Referral Discount Banner - if applied */}
            {team.referral_discount > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Gift className="text-emerald-600" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-700">Referral Discount Applied!</p>
                    <p className="text-emerald-600 text-sm">
                      You saved ₹{team.referral_discount} using code <span className="font-mono font-bold">{team.referral_code}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Apply Referral Code Banner - for pending teams without referral */}
            {team.payment_status === 'pending' && !team.referral_code && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-100 p-2 rounded-lg">
                      <Gift className="text-emerald-600" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-emerald-700">Have a Referral Code?</p>
                      <p className="text-emerald-600 text-sm">
                        Get ₹100 off per member! Save ₹{team.total_members * 100} on your registration.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReferralModal(true)}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
                    data-testid="apply-referral-btn"
                  >
                    <Gift size={16} />
                    Apply Code
                  </button>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Team Members" value={team.total_members} color="blue" />
              <StatCard 
                icon={CreditCard} 
                label="Amount" 
                value={`₹${team.total_price}`} 
                color="green" 
                subtext={team.referral_discount > 0 ? `₹${team.referral_discount} discount applied` : `₹${team.price_per_person}/person`} 
              />
              <StatCard icon={Award} label="Theme" value={team.hackathon_theme?.split(' ')[0] || 'N/A'} color="purple" />
              <StatCard icon={Ticket} label="Status" value={team.payment_status === 'completed' ? 'Confirmed' : 'Pending'} color={team.payment_status === 'completed' ? 'green' : 'yellow'} />
            </div>

            {/* Add Team Members CTA - Prominent Banner */}
            {team.total_members < 10 && (
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-4 shadow-md">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-3 rounded-lg">
                      <UserPlus className="text-white" size={24} />
                    </div>
                    <div className="text-white text-center sm:text-left">
                      <p className="font-semibold text-lg">Add Team Members</p>
                      <p className="text-blue-100 text-sm">
                        {10 - team.total_members} slot{10 - team.total_members !== 1 ? 's' : ''} available • Max 10 members allowed
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={startEdit}
                    className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2 shadow-sm"
                    data-testid="add-members-btn"
                  >
                    <Plus size={18} />
                    Add Members
                  </button>
                </div>
              </div>
            )}

            {/* Team Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{team.team_name}</h2>
                  <p className="text-gray-500 text-sm mt-1">Registration #: {team.registration_number}</p>
                </div>
                <div className="flex gap-2">
                  {team.payment_status === 'pending' && (
                    <>
                      <button onClick={startEdit} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                        <Edit size={16} /> Edit Team
                      </button>
                      <button onClick={handleDeleteTeam} className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2">
                        <Trash2 size={16} /> Delete
                      </button>
                    </>
                  )}
                  {(team.payment_status === 'completed' || team.payment_status === 'partial') && team.total_members < 10 && (
                    <button onClick={startEdit} className="px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center gap-2">
                      <UserPlus size={16} /> Add Members
                    </button>
                  )}
                </div>
              </div>

              {/* Payment Required Alert */}
              {team.payment_status === 'pending' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-semibold text-yellow-800">Payment Required</p>
                      <p className="text-yellow-700 text-sm mt-1">Complete payment to confirm your registration</p>
                      <p className="text-yellow-800 font-bold mt-2">Amount: ₹{team.total_price}</p>
                    </div>
                    <button
                      onClick={() => { setPaymentType('team_registration'); setShowPaymentModal(true); }}
                      className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-700 flex items-center gap-2"
                    >
                      <CreditCard size={16} /> Pay Now
                    </button>
                  </div>
                </div>
              )}

              {/* Partial Payment Alert */}
              {team.payment_status === 'partial' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-semibold text-orange-800">Additional Payment Required</p>
                      <p className="text-orange-700 text-sm mt-1">
                        You have added {team.total_members - (team.paid_member_count || 0)} new member(s)
                      </p>
                      <p className="text-orange-800 font-bold mt-2">Amount Due: ₹{team.amount_due || 0}</p>
                    </div>
                    <button
                      onClick={() => { setPaymentType('team_registration'); setShowPaymentModal(true); }}
                      className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 flex items-center gap-2"
                    >
                      <CreditCard size={16} /> Pay Now
                    </button>
                  </div>
                </div>
              )}

              {/* Payment Confirmed */}
              {team.payment_status === 'completed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="flex-1">
                      <p className="font-semibold text-green-800">Registration Confirmed!</p>
                      <p className="text-green-700 text-sm mt-1">Your team is registered for EvalentX 2026</p>
                    </div>
                    <button
                      onClick={fetchConfirmationLetter}
                      disabled={loadingLetter}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                    >
                      <FileText size={16} /> {loadingLetter ? 'Loading...' : 'Confirmation Letter'}
                    </button>
                  </div>
                </div>
              )}

              {/* Open Innovation Idea */}
              {team.hackathon_theme === 'Open Innovation' && team.idea_description && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Project Idea</h3>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{team.idea_description}</p>
                </div>
              )}

              {/* Quick Member Preview */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Team Members</h3>
                <div className="flex flex-wrap gap-2">
                  {team.members.slice(0, 4).map((member, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-full">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-sm text-gray-700">{member.name}</span>
                    </div>
                  ))}
                  {team.members.length > 4 && (
                    <button 
                      onClick={() => setActiveTab('team')}
                      className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1"
                    >
                      +{team.members.length - 4} more <ChevronRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Event Info */}
            {eventInfo && (
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white shadow-md">
                <h3 className="font-semibold mb-4">Event Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-blue-200 text-xs uppercase tracking-wide">Registrations</p>
                    <p className="text-2xl font-bold">{eventInfo.current_registrations}</p>
                  </div>
                  <div>
                    <p className="text-blue-200 text-xs uppercase tracking-wide">Price/Person</p>
                    <p className="text-2xl font-bold">₹{eventInfo.current_price_per_person}</p>
                  </div>
                  <div>
                    <p className="text-blue-200 text-xs uppercase tracking-wide">Early Bird</p>
                    <p className="text-xl font-bold">{eventInfo.early_bird_available ? '✓ Active' : '✗ Closed'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Team Intent Form - Only for completed payment */}
            {team.payment_status === 'completed' && (
              <TeamIntentForm team={team} />
            )}
          </div>
        )}

        {/* Team Details Tab */}
        {team && !showCreateForm && !showEditForm && activeTab === 'team' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Team Members</h2>
                  <p className="text-gray-500 text-sm">{team.total_members} members registered</p>
                </div>
                {(team.payment_status === 'completed' || team.payment_status === 'partial') && (
                  <button onClick={startEdit} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
                    <UserPlus size={16} /> Add Member
                  </button>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {team.members.map((member, index) => (
                  <MemberCard 
                    key={index} 
                    member={member} 
                    index={index} 
                    certificates={memberCertificates}
                    backendUrl={BACKEND_URL}
                  />
                ))}
              </div>
            </div>

            {/* Team Details Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Registration Details</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Team Name</p>
                  <p className="font-semibold text-gray-900 mt-1">{team.team_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Registration #</p>
                  <p className="font-mono text-gray-900 mt-1 text-sm">{team.registration_number || team.id}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Hackathon Theme</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="font-semibold text-gray-900">{team.hackathon_theme}</p>
                    {team.payment_status === 'completed' && !team.problem_statement_locked && (
                      <button
                        onClick={openThemeChangeModal}
                        className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors flex items-center gap-1"
                        data-testid="change-theme-btn"
                      >
                        <Edit size={12} />
                        Change
                      </button>
                    )}
                  </div>
                  {team.problem_statement_locked && team.payment_status === 'completed' && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertCircle size={10} />
                      Theme locked (problem statement selected)
                    </p>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</p>
                  <p className="font-semibold text-gray-900 mt-1">₹{team.total_price}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Payment Status</p>
                  <PaymentStatusBadge status={team.payment_status} />
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
                  <p className="font-semibold text-gray-900 mt-1">{new Date(team.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
            
            {/* Team Management Section - Invite/Remove Members */}
            {isTeamLeader && (
              <TeamManagement 
                team={team} 
                onUpdate={fetchTeamAndEventInfo} 
                isLeader={isTeamLeader} 
              />
            )}
            
            {/* Past Participation History */}
            {participationHistory && participationHistory.past_participations?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <History size={20} className="text-purple-600" />
                  Past Participation History
                </h3>
                <div className="space-y-3">
                  {participationHistory.past_participations.map((participation, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                          {participation.edition}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{participation.team_name}</p>
                          <p className="text-sm text-gray-500">Edition {participation.edition} • {participation.hackathon_theme}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {participation.is_leader && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            Leader
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          participation.payment_status === 'completed' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {participation.payment_status === 'completed' ? 'Confirmed' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Submissions Tab */}
        {team && !showCreateForm && !showEditForm && activeTab === 'submissions' && team.payment_status === 'completed' && (
          <TeamSubmissionSection team={team} />
        )}

        {/* Bookings Tab */}
        {team && !showCreateForm && !showEditForm && activeTab === 'bookings' && team.payment_status === 'completed' && (
          <AccommodationBooking team={team} />
        )}

        {/* Edit Team Form */}
        {showEditForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {team?.payment_status === 'completed' || team?.payment_status === 'partial' ? 'Add More Members' : 'Edit Team'}
              </h2>
              <button onClick={() => setShowEditForm(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            {(team?.payment_status === 'completed' || team?.payment_status === 'partial') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm flex items-center gap-2">
                  <AlertCircle size={16} />
                  Payment completed. You can only add more members. Team name and theme cannot be changed.
                </p>
              </div>
            )}

            <form onSubmit={handleUpdateTeam} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Name *</label>
                  <input
                    type="text"
                    value={formData.team_name}
                    onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={team?.payment_status === 'completed' || team?.payment_status === 'partial'}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hackathon Theme *</label>
                  <select
                    value={formData.hackathon_theme}
                    onChange={(e) => setFormData({ ...formData, hackathon_theme: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={team?.payment_status === 'completed' || team?.payment_status === 'partial'}
                    required
                  >
                    <option value="">Select Theme</option>
                    <option value="Artificial Intelligence">Artificial Intelligence</option>
                    <option value="Blockchain">Blockchain</option>
                    <option value="Open Innovation">Open Innovation</option>
                  </select>
                </div>
              </div>

              {formData.hackathon_theme === 'Open Innovation' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Describe Your Idea *</label>
                  <textarea
                    value={formData.idea_description}
                    onChange={(e) => setFormData({ ...formData, idea_description: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[150px]"
                    placeholder="Describe your innovative idea..."
                    required
                  />
                </div>
              )}

              {/* Team Members */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Team Members ({formData.members.length}/10)</h3>
                  {formData.members.length < 10 && (
                    <button type="button" onClick={addMember} className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                      <Plus size={16} /> Add Member
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  {formData.members.map((member, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Member {index + 1}</span>
                        {formData.members.length > 1 && index >= (team?.paid_member_count || 0) && (
                          <button type="button" onClick={() => removeMember(index)} className="text-red-500 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <input
                          type="text"
                          placeholder="Full Name *"
                          value={member.name}
                          onChange={(e) => updateMember(index, 'name', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="email"
                          placeholder="Email *"
                          value={member.email}
                          onChange={(e) => updateMember(index, 'email', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="tel"
                          placeholder="Phone *"
                          value={member.phone}
                          onChange={(e) => updateMember(index, 'phone', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="text"
                          placeholder="College/University"
                          value={member.college}
                          onChange={(e) => updateMember(index, 'college', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                        <select
                          value={member.year || ''}
                          onChange={(e) => updateMember(index, 'year', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Year of Study</option>
                          <option value="1st Year">1st Year</option>
                          <option value="2nd Year">2nd Year</option>
                          <option value="3rd Year">3rd Year</option>
                          <option value="4th Year">4th Year</option>
                          <option value="PG">PG</option>
                          <option value="PhD">PhD</option>
                          <option value="Working Professional">Working Professional</option>
                        </select>
                        <select
                          value={member.gender || ''}
                          onChange={(e) => updateMember(index, 'gender', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                        <select
                          value={member.workshop_choice}
                          onChange={(e) => updateMember(index, 'workshop_choice', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Workshop (Optional)</option>
                          <option value="Artificial Intelligence">Artificial Intelligence</option>
                          <option value="Blockchain">Blockchain</option>
                        </select>
                        <select
                          value={member.mode}
                          onChange={(e) => updateMember(index, 'mode', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Mode (Optional)</option>
                          <option value="Offline">Offline</option>
                          <option value="Online">Online</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && team && (
          <CashfreeCheckout
            team={team}
            paymentType={paymentType}
            onSuccess={handlePaymentSuccess}
            onFailure={handlePaymentFailure}
            onClose={() => setShowPaymentModal(false)}
          />
        )}

        {/* Confirmation Letter Modal */}
        {showLetter && letterData && (
          <ConfirmationLetter
            letterData={letterData}
            onClose={() => setShowLetter(false)}
          />
        )}

        {/* Apply Referral Code Modal */}
        {showReferralModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Apply Referral Code</h3>
                <button
                  onClick={() => { setShowReferralModal(false); setReferralCodeInput(''); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                <p className="text-emerald-700 font-medium">Save ₹{team?.total_members * 100} on your registration!</p>
                <p className="text-emerald-600 text-sm mt-1">
                  ₹100 discount per member × {team?.total_members} members
                </p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Enter Referral Code</label>
                <input
                  type="text"
                  value={referralCodeInput}
                  onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g., CVJOH1234"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg font-mono uppercase focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  maxLength={12}
                  data-testid="referral-code-modal-input"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowReferralModal(false); setReferralCodeInput(''); }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyReferralCode}
                  disabled={applyingReferral || !referralCodeInput.trim()}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="apply-referral-submit"
                >
                  {applyingReferral ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Applying...
                    </>
                  ) : (
                    <>
                      <Gift size={18} />
                      Apply & Save ₹{team?.total_members * 100}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Theme Change Modal */}
        {showThemeChangeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Settings className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Change Hackathon Theme</h3>
                    <p className="text-sm text-gray-500">Select a new domain/theme for your project</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowThemeChangeModal(false); setNewTheme(''); setNewIdeaDescription(''); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="close-theme-modal"
                >
                  <X size={24} />
                </button>
              </div>
              
              {/* Body */}
              <div className="p-6 overflow-y-auto">
                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
                    <div>
                      <p className="font-medium text-amber-800">Important</p>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1 list-disc list-inside">
                        <li>Theme change will not affect your payment or submissions</li>
                        <li>If you've selected a problem statement (not locked), it will be cleared</li>
                        <li>You cannot change theme after locking a problem statement</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Current Theme */}
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-1">Current Theme</p>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg font-medium">
                      {team?.hackathon_theme}
                    </span>
                    <ChevronRight className="text-gray-400" size={20} />
                    {newTheme && newTheme !== team?.hackathon_theme && (
                      <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg font-medium">
                        {newTheme}
                      </span>
                    )}
                  </div>
                </div>

                {/* Theme Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select New Theme *</label>
                  <select
                    value={newTheme}
                    onChange={(e) => setNewTheme(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="new-theme-select"
                  >
                    <option value="">Select Theme</option>
                    <option value="Artificial Intelligence">Artificial Intelligence</option>
                    <option value="Blockchain">Blockchain</option>
                    <option value="Open Innovation">Open Innovation (Your Own Idea)</option>
                  </select>
                </div>

                {/* Idea Description for Open Innovation */}
                {newTheme === 'Open Innovation' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Describe Your Idea * 
                      <span className="text-gray-400 font-normal ml-1">
                        ({newIdeaDescription.split(/\s+/).filter(Boolean).length}/50-1000 words)
                      </span>
                    </label>
                    <textarea
                      value={newIdeaDescription}
                      onChange={(e) => setNewIdeaDescription(e.target.value)}
                      placeholder="Describe your innovative idea in detail... (minimum 50 words)"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[150px]"
                      data-testid="new-idea-description"
                    />
                    {newIdeaDescription && newIdeaDescription.split(/\s+/).filter(Boolean).length < 50 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Please write at least 50 words ({50 - newIdeaDescription.split(/\s+/).filter(Boolean).length} more needed)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
                <button
                  onClick={() => { setShowThemeChangeModal(false); setNewTheme(''); setNewIdeaDescription(''); }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleThemeChange}
                  disabled={changingTheme || !newTheme || newTheme === team?.hackathon_theme || (newTheme === 'Open Innovation' && (!newIdeaDescription || newIdeaDescription.split(/\s+/).filter(Boolean).length < 50))}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-theme-change"
                >
                  {changingTheme ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Changing...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Confirm Theme Change
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
